/**
 * ContactStep — email and phone. ONB-5, and not skippable.
 *
 * WHY IT IS REQUIRED
 * Email already exists on the account (it is how they signed in), so
 * this screen is really about the phone — and about confirming the
 * address every invoice, gallery link and client email will be sent
 * from before any of those exist. Both are things a studio would have
 * to be chased for later, which is the whole argument for asking now.
 *
 * WHY THE PHONE IS NOT NORMALISED
 * Orbit's studios are international and nothing here knows their
 * country. Rewriting a number their clients actually call, into a
 * shape we guessed, is worse than storing the string they trust. The
 * only validation is "enough digits to be a phone number".
 */
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { StepIntro } from './StepIntro';

/** Digits only, ignoring punctuation. Short enough to accept every
 *  national format, long enough to reject a typo'd fragment. */
export const isPlausiblePhone = (v: string) => (v.match(/\d/g)?.length ?? 0) >= 7;
export const isPlausibleEmail = (v: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v.trim());

export function ContactStep({
  email,
  phone,
  onEmail,
  onPhone,
  errors,
  disabled = false,
}: {
  email: string;
  phone: string;
  onEmail: (v: string) => void;
  onPhone: (v: string) => void;
  errors: { email?: string; phone?: string };
  disabled?: boolean;
}) {
  return (
    <div className="space-y-6">
      <StepIntro mood="curious">
        So your clients can reach you, and so Orbit knows where to send the
        things it sends on your behalf.
      </StepIntro>

      <div className="space-y-2">
        <Label htmlFor="onb-email" className="text-[color:var(--g-ink)]">
          Email
        </Label>
        <Input
          id="onb-email"
          type="email"
          inputMode="email"
          autoComplete="email"
          value={email}
          onChange={(e) => onEmail(e.target.value)}
          placeholder="you@yourstudio.com"
          disabled={disabled}
          aria-invalid={!!errors.email}
          className="h-14 rounded-2xl border-[color:var(--g-line)] bg-[color:var(--g-fill)] text-[17px] text-[color:var(--g-ink)] placeholder:text-[color:var(--g-mute)]"
        />
        {errors.email && <p className="text-sm text-destructive">{errors.email}</p>}
      </div>

      <div className="space-y-2">
        <Label htmlFor="onb-phone" className="text-[color:var(--g-ink)]">
          Phone
        </Label>
        <Input
          id="onb-phone"
          type="tel"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(e) => onPhone(e.target.value)}
          placeholder="Any format — we keep it exactly as you type it"
          disabled={disabled}
          aria-invalid={!!errors.phone}
          className="h-14 rounded-2xl border-[color:var(--g-line)] bg-[color:var(--g-fill)] text-[17px] text-[color:var(--g-ink)] placeholder:text-[color:var(--g-mute)]"
        />
        {errors.phone && <p className="text-sm text-destructive">{errors.phone}</p>}
      </div>

      <p className="text-xs leading-relaxed text-[color:var(--g-mute)] opacity-80">
        Neither is ever shown to anyone you don't send it to.
      </p>
    </div>
  );
}

export default ContactStep;
